import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import type { WarehouseMapRow } from './warehouseLayer';

/** Warehouse detail drawer — master data fields only. */
export function WarehouseSummaryPanel({
  warehouse,
  onClose,
}: {
  warehouse: WarehouseMapRow;
  onClose: () => void;
}) {
  const rid = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    document.getElementById(`${rid}-close`)?.focus();
  }, [rid, warehouse.id]);

  const verified = warehouse.lastVerified
    ? new Date(warehouse.lastVerified).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : 'N/A';

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[55] bg-black/45 sm:bg-black/30"
        aria-label="Close warehouse summary"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${rid}-title`}
        data-panel="warehouse-summary"
        className={`fixed inset-y-0 right-0 z-[60] flex w-full max-w-sm flex-col border-l ${shell.border} ${shell.surfaceElevated} shadow-2xl`}
      >
        <header className={`sticky top-0 z-10 border-b ${shell.border} px-4 py-3`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={`text-[10px] font-medium uppercase tracking-widest ${shell.accent}`}>
                Warehouse Summary
              </p>
              <h3
                id={`${rid}-title`}
                className="mt-0.5 truncate font-mono text-lg font-semibold text-[var(--shell-fg)]"
              >
                {warehouse.code}
              </h3>
              <p className={`truncate text-sm ${shell.muted}`}>{warehouse.name}</p>
            </div>
            <button
              id={`${rid}-close`}
              type="button"
              className={`${shell.controlIcon} ${shell.focusRing}`}
              onClick={onClose}
              aria-label="Close warehouse summary"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          <dl className="grid grid-cols-1 gap-3">
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>City</dt>
              <dd className="text-sm text-[var(--shell-fg)]">{warehouse.city || 'N/A'}</dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>State</dt>
              <dd className="text-sm text-[var(--shell-fg)]">
                {warehouse.stateLabel || 'N/A'}
                {warehouse.stateCode ? ` (${warehouse.stateCode})` : ''}
              </dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>Region</dt>
              <dd className="text-sm text-[var(--shell-fg)]">{warehouse.region || 'N/A'}</dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>Warehouse type</dt>
              <dd className="font-mono text-sm text-[var(--shell-fg)]">{warehouse.warehouseType}</dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>Status</dt>
              <dd className="text-sm text-[var(--shell-fg)]">{warehouse.status}</dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>Last verified</dt>
              <dd className="text-sm text-[var(--shell-fg)]">{verified}</dd>
            </div>
            <div>
              <dt className={`text-[10px] uppercase ${shell.subtle}`}>Coordinates</dt>
              <dd className="font-mono text-xs text-[var(--shell-fg)]">
                {warehouse.hasCoords
                  ? `${warehouse.latitude!.toFixed(5)}, ${warehouse.longitude!.toFixed(5)}`
                  : 'Not set'}
              </dd>
            </div>
          </dl>
        </div>

        <footer className={`sticky bottom-0 border-t ${shell.border} px-3 py-2.5`}>
          <Link
            to="/settings/master-data/warehouses"
            className={`${shell.control} ${shell.focusRing} inline-flex min-h-[44px] items-center`}
          >
            Open Warehouse Master
          </Link>
        </footer>
      </aside>
    </>
  );
}
